import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partyApi, type PartyData, type PartyInviteBrief, type PartyNotice } from '../auth/authApi';
import { useAuthStore } from '../auth/authStore';

/**
 * Shared party/invite polling. PartySection calls this itself when used
 * standalone (dashboard); HubMap calls it once and passes the result down as
 * `partyState` to PartySection (embedded in HubPartyDock) so the Hub map's
 * own portal-gathering logic and PartySection's UI read from the exact same
 * poll instead of doubling up on GET /api/party.
 *
 * Handles the two cross-cutting concerns every consumer needs:
 *  - auto-navigate everyone to /play the instant the party's combat starts
 *    (my_entity_id + combat_id show up in the poll response)
 *  - surface `notices` (e.g. "X declined your invite") exactly once, then
 *    ack them so they don't repeat on the next poll
 */
export function useParty(token: string | null | undefined) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const myUserId = user?.id ?? '';

  const [party, setParty] = useState<PartyData | null>(null);
  const [invite, setInvite] = useState<PartyInviteBrief | null>(null);
  const [notices, setNotices] = useState<PartyNotice[]>([]);
  const [loading, setLoading] = useState(true);

  const navigatedCombatRef = useRef<string | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      const res = await partyApi.get(token);
      setParty(res.party);
      setInvite(res.invite);

      if (res.party?.notices?.length) {
        setNotices(prev => [...prev, ...res.party!.notices]);
        partyApi.ackNotices(token).catch(() => {});
      }

      // Auto-navigate when the battle starts — all members receive the combat_id.
      const myEntityIdForNav =
        res.party?.my_entity_id ??
        (res.party?.entity_map?.[myUserId] || null);

      if (
        res.party?.status === 'in_combat' &&
        myEntityIdForNav &&
        res.party.combat_id &&
        navigatedCombatRef.current !== res.party.combat_id &&
        sessionStorage.getItem('abandoned_combat') !== res.party.combat_id
      ) {
        navigatedCombatRef.current = res.party.combat_id;
        navigate(`/play?combat_id=${res.party.combat_id}&entity_id=${myEntityIdForNav}`);
      }
    } catch {
      // ignore transient failures
    } finally {
      setLoading(false);
    }
  }, [token, navigate, myUserId]);

  useEffect(() => {
    if (!token) return;
    reload();
    const id = setInterval(reload, 3000);
    return () => clearInterval(id);
  }, [reload, token]);

  /** Consumers call this right after showing the notices (e.g. as a toast). */
  const consumeNotices = useCallback(() => setNotices([]), []);

  /**
   * Consumers that navigate to combat themselves (e.g. the "Start" button's
   * own immediate navigate, for a snappier feel than waiting for the next
   * poll) call this right after, so the poll's auto-navigate doesn't fire a
   * second, redundant navigate to the same combat on its next tick.
   */
  const markNavigated = useCallback((combatId: string) => {
    navigatedCombatRef.current = combatId;
  }, []);

  return { party, invite, notices, loading, reload, consumeNotices, markNavigated, myUserId };
}

export type UsePartyResult = ReturnType<typeof useParty>;
